<script setup lang="ts">
import { useSites } from '@/composables/core/sites'
import { SiteType, type LiteSite } from '@/types/core/Site'
import type { ILiteSiteFilters } from '@/types/core/filters'
import { Icon } from '@iconify/vue'
import { ElMessage } from 'element-plus'
import type { OptionType } from 'element-plus/es/components/select-v2/src/select.types.mjs'
import type { SelectV2Instance } from 'element-plus'
import { computed, onBeforeMount, ref, useTemplateRef, watchEffect } from 'vue'

export interface ISiteIdentifier {
  site_id?: string
  site_name?: string
  timezone?: string
  type?: string
}

const selectedSite = defineModel<ISiteIdentifier>()

const props = withDefaults(
  defineProps<{
    placeholder?: string
    type?: SiteType
    customerId?: number
    defaultFirstRemoteOption?: boolean
    disabled?: boolean
  }>(),
  {
    placeholder: 'Select a site...',
    type: undefined,
    customerId: undefined,
    defaultFirstRemoteOption: false,
  }
)

const elSelectV2Ref = useTemplateRef<SelectV2Instance>('elSelectV2Ref')

const searchQuery = ref('')
const initialLoadRemote = ref<boolean>(false)

const filters = computed<ILiteSiteFilters>(() => ({
  ...(props.type && { type: props.type }),
  ...(props.customerId && { customer_id: props.customerId }),
  ...(searchQuery.value && { fragment: searchQuery.value }),
}))

const { getAllSitesQuery } = useSites()
const { data: queryData, isFetching: queryIsFetching, isError: queryIsError, suspense } = getAllSitesQuery(filters)

const siteOptions = computed<OptionType[]>(() => {
  const mappedQueryData =
    queryData.value?.map<OptionType>((site: LiteSite) => {
      return {
        label: site.site_name,
        value: site,
      }
    }) ?? []

  if (selectedSite.value && !mappedQueryData.find((item) => item.value.site_id === selectedSite.value?.site_id)) {
    mappedQueryData.push({
      label: selectedSite.value?.site_name || selectedSite.value?.site_id || 'Unknown Site',
      value: selectedSite.value,
    })
  }

  return mappedQueryData
})

// Show error message when sites query errors
watchEffect(() => {
  if (queryIsError.value) {
    ElMessage.error('Failed to fetch sites.')
  }
})

/**
 * Filters sites by changing Vue Query key dependencies.
 *
 * @param {string} query fragment query string.
 */
const filterSites = (query: string) => {
  if (query !== '') {
    searchQuery.value = query
  }
}

/**
 * Load default first (last) option.
 */
onBeforeMount(async () => {
  if (!selectedSite.value && props.defaultFirstRemoteOption) {
    initialLoadRemote.value = true
    filterSites('') // force options to load
    await suspense()
    selectedSite.value = siteOptions.value.at(-1)?.value
    initialLoadRemote.value = false
  }
})

defineExpose({
  isFetching: queryIsFetching,
  focus: () => elSelectV2Ref.value?.focus(),
})
</script>

<template>
  <el-select-v2
    ref="elSelectV2Ref"
    v-model="selectedSite"
    :options="siteOptions"
    remote
    :remote-method="filterSites"
    filterable
    clearable
    value-key="site_id"
    :placeholder="initialLoadRemote ? 'Loading...' : props.placeholder"
    :loading="queryIsFetching && siteOptions.length === 0"
    loading-text="Loading sites..."
    :disabled="initialLoadRemote || disabled"
  >
    <template #prefix>
      <el-icon>
        <Icon icon="mdi:location-outline" />
      </el-icon>
    </template>
  </el-select-v2>
</template>
